/**
 * The Finish Line (#1710 engine, #1714 job page): the read, and the switch.
 *
 * The server computes a job's promises (`GET /api/jobs/:id/finish-line`, gated
 * by `office_review.manage`). These types mirror
 * `src/server/services/finishLine/types.ts`, the API contract; keep them in
 * step when that file changes.
 *
 * The query key sits UNDER `["job", id]` on purpose: every place the job page
 * already invalidates `["job", id]` (a status change, a check-up fix, a
 * device edit) re-reads the Finish Line too, by react-query's prefix match.
 */
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { useUserPreference } from "@/lib/user-preference";
import type { CheckupFix } from "@/lib/use-job-checkup";

export type Phase = "before_visit" | "on_site" | "after_visit" | "money";
export const PHASES: Phase[] = ["before_visit", "on_site", "after_visit", "money"];

export type Owner = "office" | "tech" | "customer" | "city" | "vendor";
export type PromiseState = "kept" | "open" | "blocked" | "waiting" | "missed" | "not_applicable" | "later";

export interface PromiseProof {
    label: string;
    href?: string;
    at?: string;
}

export interface PromiseAction {
    /** `sent_another_way` (2026-10-07): opens "Already sent another way". */
    kind: CheckupFix["kind"] | "link" | "rule_check" | "sent_another_way";
    label: string;
    href?: string;
    fix?: CheckupFix;
    /** `rule_check` (#1738): the taught rule to mark kept on this job. */
    ruleId?: string;
}

/** Teach it once (#1738): the rule that made or shaped a promise. Mirrors the server's `PromiseRuleMeta`. */
export type RuleScope = "customer" | "site" | "water_authority" | "job_type";
export type RuleKind = "checklist" | "delivery_route" | "stamp_required";
export type DeliveryRoute = "portal" | "mail" | "contact";

export interface PromiseRuleMeta {
    id: string;
    kind: RuleKind;
    scopeType: RuleScope;
    scopeName: string | null;
    taughtBy: string | null;
    taughtAt: string;
    sourceJobId: string | null;
    sourceJobNumber: string | null;
    check: { by: string | null; at: string; note: string | null } | null;
}

export interface JobPromise {
    id: string;
    phase: Phase;
    title: string;
    detail: string;
    owner: Owner;
    state: PromiseState;
    proof?: PromiseProof;
    since?: string;
    dueBy?: string;
    blockedBy?: string[];
    truthIds?: string[];
    action?: PromiseAction;
    /** A taught rule made or shaped this promise (#1738). Older servers omit it. */
    rule?: PromiseRuleMeta;
}

export interface Truth {
    id: string;
    rule: string;
    promiseId: string;
    sentence: string;
}

export interface StateCounts {
    kept: number;
    onUs: number;
    blocked: number;
    waiting: number;
    total: number;
    /** Owed after the visit; not in total. Older servers omit it. */
    later?: number;
}

export interface FinishLineNext {
    promiseId: string;
    unblocks: string[];
    path: string[];
}

export interface FinishLine {
    jobId: string;
    generatedAt: string;
    promises: JobPromise[];
    truths: Truth[];
    counts: StateCounts & { byPhase: Record<Phase, StateCounts> };
    next: FinishLineNext | null;
}

export const finishLineKey = (jobId: string | undefined) => ["job", jobId, "finish-line"] as const;

/** The read. `retry: false` so a 403 (no office_review.manage) lands at once
 *  and the page falls back to today's top instead of waiting on retries. */
export function useJobFinishLine(jobId: string | undefined, enabled: boolean) {
    return useQuery<FinishLine>({
        queryKey: finishLineKey(jobId),
        queryFn: async () => (await api.get<{ finishLine: FinishLine }>(`/api/jobs/${jobId}/finish-line`)).finishLine,
        enabled: !!jobId && enabled,
        staleTime: 15_000,
        retry: false,
    });
}

/**
 * The on/off switch: a per-user preference (`user_preferences`, the same
 * server-backed store as the chapter toggles), default OFF. It follows the
 * user across machines, needs no migration and no server change, and only
 * someone who can read the Finish Line (office_review.manage) sees it.
 */
export const FINISH_LINE_PREF_KEY = "job-page.finish-line";

export function useFinishLineSwitch(): { allowed: boolean; on: boolean; set: (on: boolean) => void } {
    const { can } = useAuth();
    const allowed = can(PERMISSIONS.officeReview.manage);
    const [value, set] = useUserPreference<boolean>(FINISH_LINE_PREF_KEY, false, {
        onError: () => toast.error("Couldn't save the job page switch. It's back the way it was."),
    });
    return { allowed, on: allowed && value === true, set };
}

export type FinishLineTopState = "off" | "loading" | "on";

/**
 * Which top the job page draws. OFF unless the switch is on AND the read
 * worked: a 403, a 500 or a network error all fall back to today's UI, so the
 * Finish Line can never leave a job page with no way forward.
 */
export function useFinishLineTop(jobId: string | undefined): {
    state: FinishLineTopState;
    finishLine: FinishLine | null;
    switchAllowed: boolean;
    switchOn: boolean;
    setSwitch: (on: boolean) => void;
} {
    const sw = useFinishLineSwitch();
    // threads/job-page-fresh (10/28): the switch now opens the page rebuilt
    // from a clean sheet (pages/jobs/job-page-router.tsx), so this page only
    // renders for someone with the switch on when they asked for the classic
    // page once (`?page=classic`). Then it IS the classic page: no Finish Line.
    // Read from the address bar, not the router, so the hook keeps working
    // outside a <Router> (tests, previews).
    const classicOnce =
        typeof window !== "undefined" && new URLSearchParams(window.location.search).get("page") === "classic";
    const on = sw.on && !classicOnce;
    const q = useJobFinishLine(jobId, on);
    const state: FinishLineTopState = !on || q.isError ? "off" : q.data ? "on" : "loading";
    return {
        state,
        finishLine: state === "on" ? (q.data ?? null) : null,
        switchAllowed: sw.allowed,
        switchOn: on,
        setSwitch: (next: boolean) => {
            sw.set(next);
            // Leaving the one-time classic view for the new page: drop ?page=classic.
            // (A beat later, so the switch's save isn't cut off by the reload.)
            if (classicOnce && next) setTimeout(() => window.location.replace(window.location.pathname), 700);
        },
    };
}
